import { cn } from '@/lib/cn';
import { Activity, AlertTriangle, CheckCircle2 } from 'lucide-react';
import { PH_DIAL_THRESHOLDS } from '@/lib/constants';

interface PHDialDisplayProps {
  morningPH: number | null | undefined;
  afternoonPH: number | null | undefined;
  className?: string;
}

export function PHDialDisplay({ morningPH, afternoonPH, className }: PHDialDisplayProps) {
  // Can only calculate if both values exist
  const canCalculate =
    morningPH !== null &&
    morningPH !== undefined &&
    afternoonPH !== null &&
    afternoonPH !== undefined;

  const dialValue = canCalculate ? afternoonPH - morningPH : null;
  const absValue = dialValue !== null ? Math.abs(dialValue) : null;

  // Determine status
  type Status = 'ok' | 'warning' | 'critical' | 'pending';
  let status: Status = 'pending';
  if (absValue !== null) {
    if (absValue <= PH_DIAL_THRESHOLDS.ok) {
      status = 'ok';
    } else if (absValue <= PH_DIAL_THRESHOLDS.warning) {
      status = 'warning';
    } else {
      status = 'critical';
    }
  }

  const statusConfig = {
    ok: {
      bg: 'from-emerald-50 to-green-50',
      border: 'border-emerald-200',
      text: 'text-emerald-700',
      accent: 'text-emerald-600',
      icon: CheckCircle2,
      message: 'pH swing is within normal range',
    },
    warning: {
      bg: 'from-amber-50 to-yellow-50',
      border: 'border-amber-200',
      text: 'text-amber-700',
      accent: 'text-amber-600',
      icon: AlertTriangle,
      message: 'pH swing is elevated - monitor closely',
    },
    critical: {
      bg: 'from-red-50 to-orange-50',
      border: 'border-red-200',
      text: 'text-red-700',
      accent: 'text-red-600',
      icon: AlertTriangle,
      message: 'High pH swing - check aeration and algae',
    },
    pending: {
      bg: 'from-gray-50 to-slate-50',
      border: 'border-gray-200',
      text: 'text-gray-500',
      accent: 'text-gray-400',
      icon: Activity,
      message: 'Enter both 7am and 3pm pH to calculate',
    },
  };

  const config = statusConfig[status];
  const StatusIcon = config.icon;

  return (
    <div
      className={cn(
        'relative overflow-hidden rounded-xl border-2',
        `bg-gradient-to-br ${config.bg}`,
        config.border,
        className
      )}
    >
      {/* Decorative pattern */}
      <div className="absolute inset-0 opacity-[0.03]">
        <svg className="w-full h-full" xmlns="http://www.w3.org/2000/svg">
          <defs>
            <pattern id="ph-grid" width="20" height="20" patternUnits="userSpaceOnUse">
              <circle cx="10" cy="10" r="1" fill="currentColor" />
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#ph-grid)" />
        </svg>
      </div>

      <div className="relative p-5">
        <div className="flex items-center justify-between gap-4">
          {/* Left: Label and values */}
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <Activity className={cn('w-5 h-5', config.accent)} />
              <h3 className={cn('font-semibold', config.text)}>pH swing</h3>
            </div>

            <div className="flex items-baseline gap-3 text-sm">
              <div className={cn('space-x-1', canCalculate ? 'text-gray-600' : 'text-gray-400')}>
                <span>7am:</span>
                <span className="font-medium tabular-nums">
                  {morningPH !== null && morningPH !== undefined ? morningPH.toFixed(2) : '—'}
                </span>
              </div>
              <span className="text-gray-300">→</span>
              <div className={cn('space-x-1', canCalculate ? 'text-gray-600' : 'text-gray-400')}>
                <span>3pm:</span>
                <span className="font-medium tabular-nums">
                  {afternoonPH !== null && afternoonPH !== undefined ? afternoonPH.toFixed(2) : '—'}
                </span>
              </div>
            </div>
          </div>

          {/* Right: Dial value */}
          <div className="text-right">
            <div
              className={cn(
                'text-4xl font-bold tabular-nums tracking-tight',
                config.text
              )}
            >
              {dialValue !== null ? (
                <>
                  {dialValue > 0 ? '+' : ''}
                  {dialValue.toFixed(2)}
                </>
              ) : (
                <span className="text-gray-300">—</span>
              )}
            </div>
          </div>
        </div>

        {/* Status message */}
        <div
          className={cn(
            'mt-4 pt-4 border-t flex items-center gap-2',
            status === 'pending' ? 'border-gray-200' : config.border.replace('border-', 'border-t-')
          )}
        >
          <StatusIcon className={cn('w-4 h-4 shrink-0', config.accent)} />
          <p className={cn('text-sm', config.text)}>{config.message}</p>
        </div>
      </div>
    </div>
  );
}
